import {
  Button,
  EmptyState,
  Flex,
  Link,
  PlusIcon,
  WarningIcon,
} from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { OpenChannelStoreFrontTrigger } from '~publish/components/OpenChannelStoreFrontTrigger'

import {
  getInsightsChannelTypeLabels,
  SUPPORTED_CHANNELS_ARTICLE_URL,
} from '../utils'

import type { JSX } from 'react'

const CONNECT_CHANNEL_CTA =
  'publish-insights-allChannelsNotSupported-connectChannel-1'

type UnsupportedChannel = {
  service: string
  type: string
}

/**
 * All Channels Insights state for an org whose every connected channel is a
 * type Insights cannot report on — only Google Business Profiles, for example.
 *
 * It stands in for {@link InsightsAccessDenied} on this path. Both mean "no
 * channel is viewable", but a channel type Insights doesn't cover is not a
 * permission an admin could grant, so the access-denied copy would send an
 * actor who already owns the org to ask someone for access that doesn't exist
 * (CORE-7451). Names the type like the per-channel `InsightsNotSupported`
 * screen when the org has just one, so both routes read the same.
 *
 * The connect CTA is hidden from actors who can't manage channels, matching how
 * the no-channels empty state handles it (CORE-6008).
 */
export function AllChannelsInsightsNotSupported({
  channels,
}: {
  channels: readonly UnsupportedChannel[]
}): JSX.Element {
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const typeLabels = getInsightsChannelTypeLabels(channels)
  const singleType = typeLabels.length === 1 ? typeLabels[0] : null

  return (
    <Flex align="center" justify="center" fullHeight fullWidth>
      <EmptyState size="xlarge" variant="warning">
        <EmptyState.Icon>
          <WarningIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>
          {singleType
            ? `Insights aren't available for ${singleType}`
            : "Insights aren't available for your channels"}
        </EmptyState.Heading>
        <EmptyState.Description>
          {singleType
            ? "This channel type doesn't support Insights yet."
            : 'None of your connected channel types support Insights yet.'}{' '}
          Connect a channel Insights supports to track how it performs. <br />
          <Link
            href={SUPPORTED_CHANNELS_ARTICLE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            More about supported channels
          </Link>
          .
        </EmptyState.Description>
        {canManageChannels && (
          <EmptyState.Actions>
            <OpenChannelStoreFrontTrigger cta={CONNECT_CHANNEL_CTA}>
              <Button>
                <PlusIcon />
                Connect a Channel
              </Button>
            </OpenChannelStoreFrontTrigger>
          </EmptyState.Actions>
        )}
      </EmptyState>
    </Flex>
  )
}
